import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { getCart, cartTotal, formatPrice, clearCart } from "../lib/cart";
import { api } from "../lib/api";
import { toast } from "sonner";
import Logo from "../components/Logo";
import { LangToggle, useLang } from "../lib/i18n";
import { getCheckoutContent } from "../lib/checkoutContent";

export default function CheckoutPage() {
  const nav = useNavigate();
  const { lang } = useLang();
  const cc = getCheckoutContent(lang);
  const [items] = useState(getCart());
  const [config, setConfig] = useState(null);
  const cardObj = useRef(null);
  const cardAttached = useRef(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    first_name:"",
    last_name:"",
    email:"",
    address_line1: "",
    address_line2: "",
    city: "",
    state:"",
    postal_code: ""
  });
  const [err, setErr] = useState("");
  const [stateList, setStateList] = useState([]);
  const cardRef = useRef(null);
  const addressInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const cartCount = items.reduce((s,i)=>s+i.qty,0);

  // Selected items state - all items selected by default
  const [selectedItems, setSelectedItems] = useState(() => {
    const initial = {};
    items.forEach(item => {
      initial[item.sku] = true;
    });
    return initial;
  });

  // Add-ons state
  const [addOns, setAddOns] = useState({
    syringes: false
  });

  // Tax calculation
  const [taxRate, setTaxRate] = useState(0);
  const [taxAmount, setTaxAmount] = useState(0);

  const ADDON_PRICES = {
    syringes: 1500 // $15.00 in cents
  };

  const MANDATORY_SHIPPING = 3000; // $30.00 in cents - always included

  const toggleAddOn = (addon) => {
    setAddOns(prev => ({ ...prev, [addon]: !prev[addon] }));
  };

  const toggleItemSelection = (sku) => {
    setSelectedItems(prev => ({ ...prev, [sku]: !prev[sku] }));
  };

  const calculateSubtotal = () => {
    // Only include selected items in subtotal
    let subtotal = items
      .filter(item => selectedItems[item.sku])
      .reduce((sum, item) => sum + (item.price_cents * item.qty), 0);

    if (addOns.syringes) subtotal += ADDON_PRICES.syringes;
    subtotal += MANDATORY_SHIPPING; // Always add mandatory shipping
    return subtotal;
  };

  const calculateTotal = () => {
    return calculateSubtotal() + taxAmount;
  };

  // Fetch tax rate when state changes
  const updateTaxRate = async (stateCode) => {
    if (!stateCode) {
      setTaxRate(0);
      setTaxAmount(0);
      return;
    }
    try {
      const response = await api.get(`/tax-rate/${stateCode}`);
      setTaxRate(response.data.tax_rate);
      const subtotal = calculateSubtotal();
      const tax = Math.round(subtotal * response.data.tax_rate);
      setTaxAmount(tax);
    } catch (error) {
      console.error("Failed to fetch tax rate:", error);
      setTaxRate(0);
      setTaxAmount(0);
    }
  };

  useEffect(() => {
    // Scroll to top immediately when page loads
    window.scrollTo({ top: 0, behavior: 'instant' });

    if (items.length === 0) { nav("/cart"); return; }

    // Load parsed delivery address from cart page if available
    const parsedAddressJson = localStorage.getItem("delivery_address_parsed");
    let parsedAddress = null;
    if (parsedAddressJson) {
      try {
        parsedAddress = JSON.parse(parsedAddressJson);
        console.log("Parsed address from localStorage:", parsedAddress);
      } catch (e) {
        console.error("Failed to parse delivery address:", e);
      }
    }

    api.get("/payments/config").then(r => {
      console.log("Square config received:", r.data);
      setConfig(r.data);
    });
    api.get("/states").then(r => setStateList(r.data));
    api.get("/intake/results").then(r => {
      if (r.data.found && r.data.answers) {
        const stateCode = r.data.state_code || "";
        console.log("Setting form with intake results");
        setForm(f => ({
          ...f,
          first_name: r.data.answers.first_name || "",
          last_name: r.data.answers.last_name || "",
          email: r.data.answers.email || "",
          address_line1: parsedAddress?.address_line1 || f.address_line1,
          city: parsedAddress?.city || f.city,
          state: parsedAddress?.state || stateCode,
          postal_code: parsedAddress?.postal_code || f.postal_code
        }));
        // Fetch tax rate for pre-filled state
        const finalState = parsedAddress?.state || stateCode;
        if (finalState) {
          updateTaxRate(finalState);
        }
      } else if (parsedAddress) {
        // If no intake results, still set the parsed delivery address
        console.log("No intake results, setting parsed delivery address");
        setForm(f => ({
          ...f,
          address_line1: parsedAddress.address_line1,
          city: parsedAddress.city,
          state: parsedAddress.state,
          postal_code: parsedAddress.postal_code
        }));
        if (parsedAddress.state) {
          updateTaxRate(parsedAddress.state);
        }
      }
    }).catch(()=>{
      // If intake results fail, still set the parsed delivery address
      if (parsedAddress) {
        console.log("Intake results failed, setting parsed delivery address");
        setForm(f => ({
          ...f,
          address_line1: parsedAddress.address_line1,
          city: parsedAddress.city,
          state: parsedAddress.state,
          postal_code: parsedAddress.postal_code
        }));
        if (parsedAddress.state) {
          updateTaxRate(parsedAddress.state);
        }
      }
    });
  // eslint-disable-next-line
  }, []);

  // Recalculate tax when state or add-ons change
  useEffect(() => {
    if (form.state) {
      updateTaxRate(form.state);
    }
  // eslint-disable-next-line
  }, [form.state, addOns.syringes]);

  // Load Google Places Autocomplete for address field
  useEffect(() => {
    const googleApiKey = process.env.REACT_APP_GOOGLE_PLACES_API_KEY;

    if (!googleApiKey) {
      console.warn("Google Places API key not configured");
      return;
    }

    const loadGooglePlaces = () => {
      if (!window.google || !window.google.maps) {
        const script = document.createElement("script");
        script.src = `https://maps.googleapis.com/maps/api/js?key=${googleApiKey}&libraries=places`;
        script.async = true;
        script.defer = true;
        script.onload = initAutocomplete;
        document.head.appendChild(script);
      } else {
        initAutocomplete();
      }
    };

    const initAutocomplete = () => {
      if (!addressInputRef.current || !window.google) return;

      autocompleteRef.current = new window.google.maps.places.Autocomplete(addressInputRef.current, {
        types: ["address"],
        componentRestrictions: { country: "us" }
      });

      autocompleteRef.current.addListener("place_changed", () => {
        const place = autocompleteRef.current.getPlace();
        if (!place.address_components) return;

        let streetNumber = "";
        let route = "";
        let city = "";
        let state = "";
        let postalCode = "";

        place.address_components.forEach((component) => {
          const types = component.types;
          if (types.includes("street_number")) {
            streetNumber = component.long_name;
          }
          if (types.includes("route")) {
            route = component.long_name;
          }
          if (types.includes("locality")) {
            city = component.long_name;
          }
          if (types.includes("administrative_area_level_1")) {
            state = component.short_name;
          }
          if (types.includes("postal_code")) {
            postalCode = component.long_name;
          }
        });

        const addressLine1 = `${streetNumber} ${route}`.trim();

        setForm(f => ({
          ...f,
          address_line1: addressLine1,
          city: city,
          state: state,
          postal_code: postalCode
        }));
      });
    };

    loadGooglePlaces();
  }, []);

  // Square Web SDK — single attach guard against StrictMode double-effect
  useEffect(() => {
    if (!config || !config.application_id) return;
    let cancelled = false;
    const url = config.environment === "production"
      ? "https://web.squarecdn.com/v1/square.js"
      : "https://sandbox.web.squarecdn.com/v1/square.js";
    console.log("Square SDK URL:", url, "Environment:", config.environment);
    const init = async () => {
      if (cancelled || cardObj.current || !window.Square || !cardRef.current) return;
      cardAttached.current = true;
      try {
        // Wipe any prior card iframe (StrictMode safety)
        cardRef.current.innerHTML = "";
        const payments = window.Square.payments(config.application_id, config.location_id || undefined);
        const c = await payments.card();
        if (cancelled) return;
        await c.attach(cardRef.current);
        cardObj.current = c;
      } catch (e) {
        cardAttached.current = false;
        setErr("Could not initialize Square card form: " + (e.message || e));
      }
    };
    if (window.Square) { init(); }
    else if (!document.querySelector(`script[src="${url}"]`)) {
      const s = document.createElement("script");
      s.src = url; s.onload = init; s.onerror = () => setErr("Failed to load Square SDK");
      document.head.appendChild(s);
    } else {
      const t = setInterval(() => { if (window.Square) { clearInterval(t); init(); } }, 100);
      setTimeout(() => clearInterval(t), 8000);
    }
    return () => { cancelled = true; };
  }, [config]);

  const pay = async () => {
    setErr("");
    if (!form.first_name || !form.last_name || !form.email) { setErr(cc.errors.completeDetails); return; }
    if (!form.address_line1 || !form.city || !form.state || !form.postal_code) {
      setErr(cc.errors.completeAddress);
      return;
    }
    if (!cardObj.current) { setErr(cc.errors.cardNotReady); return; }

    // Build full shipping address
    const shippingAddress = `${form.address_line1}${form.address_line2 ? ', ' + form.address_line2 : ''}, ${form.city}, ${form.state} ${form.postal_code}`;

    setBusy(true);
    try {
      const result = await cardObj.current.tokenize();
      if (result.status !== "OK") { setErr(cc.errors.cardInvalid); setBusy(false); return; }

      // Build items array including only selected items
      const allItems = items
        .filter(i => selectedItems[i.sku])
        .map(i => ({ sku: i.sku, name: i.name, price_cents: i.price_cents, qty: i.qty }));

      // Validate at least one item is selected
      if (allItems.length === 0) {
        setErr(cc.errors.selectProduct);
        setBusy(false);
        return;
      }

      // Mandatory shipping - always included
      allItems.push({ sku: "SHIPPING", name: cc.shipping, price_cents: MANDATORY_SHIPPING, qty: 1 });

      if (addOns.syringes) {
        allItems.push({ sku: "ADDON_SYRINGES", name: cc.syringes, price_cents: ADDON_PRICES.syringes, qty: 1 });
      }

      const resp = await api.post("/checkout/create", {
        source_id: result.token,
        items: allItems,
        customer_email: form.email,
        customer_name: `${form.first_name} ${form.last_name}`,
        state_code: form.state,
        shipping_address: shippingAddress,
      });
      if (resp.data.ok) {
        clearCart();
        toast.success(cc.paymentSuccess);
        nav(`/order-success?order=${resp.data.order_id}`);
      } else {
        setErr(cc.errors.cardInvalid);
      }
    } catch (e) {
      // Parse Square API errors and show user-friendly message
      const errorData = e?.response?.data;

      // Check if it's a Square payment error
      if (errorData && (
        typeof errorData === 'object' &&
        (errorData.errors || errorData.body?.errors)
      )) {
        setErr(cc.errors.cardInvalid);
      } else if (typeof errorData === 'string' &&
                 (errorData.includes('PAN_FAILURE') ||
                  errorData.includes('INVALID_ACCOUNT') ||
                  errorData.includes('CVV_FAILURE') ||
                  errorData.includes('PAYMENT_METHOD_ERROR'))) {
        setErr(cc.errors.cardInvalid);
      } else {
        setErr(cc.errors.cardInvalid);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <nav className="hp-nav" data-testid="checkout-nav">
        <Logo size="md" linkTo="/" testId="checkout-logo"/>
        <ul className="hp-nav-links">
          <li><Link to="/">{cc.home}</Link></li>
          <li><Link to="/estore" data-testid="checkout-nav-shop">{cc.shop}</Link></li>
        </ul>
        <div className="hp-nav-right">
          <LangToggle />
          <Link to="/cart" className="hp-cart" aria-label="Cart" data-testid="checkout-nav-cart">
            🛒{cartCount > 0 && <span className="hp-cart-badge">{cartCount}</span>}
          </Link>
        </div>
      </nav>
      <div className="aa-shell" style={{paddingTop: "100px"}}>
        <h1 className="aa-h1">{cc.pageTitle}</h1>

        <h2 className="aa-h2" style={{fontSize:"1.2rem"}}>{cc.yourDetails}</h2>
        <p className="aa-sub" style={{marginBottom:"16px", fontSize:".9rem"}}>
          {cc.detailsSubtitle}
        </p>
        <div className="aa-row">
          <div><label className="aa-label">{cc.firstName}</label>
            <input
              className="aa-input"
              value={form.first_name}
              readOnly
              style={{backgroundColor:"rgba(193,127,58,.05)", cursor:"not-allowed"}}
              data-testid="co-first"
            /></div>
          <div><label className="aa-label">{cc.lastName}</label>
            <input
              className="aa-input"
              value={form.last_name}
              readOnly
              style={{backgroundColor:"rgba(193,127,58,.05)", cursor:"not-allowed"}}
              data-testid="co-last"
            /></div>
        </div>
        <div style={{marginTop:10}}>
          <label className="aa-label">{cc.email}</label>
          <input
            className="aa-input"
            type="email"
            value={form.email}
            readOnly
            style={{backgroundColor:"rgba(193,127,58,.05)", cursor:"not-allowed"}}
            data-testid="co-email"
          />
        </div>

        <hr className="aa-hr"/>
        <h2 className="aa-h2" style={{fontSize:"1.2rem"}}>{cc.shippingAddress}</h2>
        <div style={{marginTop:10}}>
          <label className="aa-label">{cc.addressLine1}</label>
          <input
            ref={addressInputRef}
            className="aa-input"
            placeholder={cc.addressLine1Placeholder}
            value={form.address_line1}
            onChange={(e)=>setForm({...form, address_line1:e.target.value})}
            data-testid="co-address1"
          />
        </div>
        <div style={{marginTop:10}}>
          <label className="aa-label">{cc.addressLine2}</label>
          <input className="aa-input" placeholder={cc.addressLine2Placeholder} value={form.address_line2} onChange={(e)=>setForm({...form, address_line2:e.target.value})} data-testid="co-address2"/>
        </div>
        <div className="aa-row" style={{marginTop:10}}>
          <div><label className="aa-label">{cc.city}</label>
            <input className="aa-input" value={form.city} onChange={(e)=>setForm({...form, city:e.target.value})} data-testid="co-city"/></div>
          <div><label className="aa-label">{cc.state}</label>
            <select className="aa-select" value={form.state} onChange={(e)=>setForm({...form, state:e.target.value})} data-testid="co-state">
              <option value="">{cc.stateSelect}</option>
              {stateList.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
            </select></div>
        </div>
        <div style={{marginTop:10}}>
          <label className="aa-label">{cc.postalCode}</label>
          <input className="aa-input" placeholder={cc.postalCodePlaceholder} value={form.postal_code} onChange={(e)=>setForm({...form, postal_code:e.target.value})} data-testid="co-postal"/>
        </div>

        <hr className="aa-hr"/>
        <h2 className="aa-h2" style={{fontSize:"1.2rem"}}>{cc.addOns}</h2>
        <p className="aa-sub" style={{marginBottom: "16px", fontSize: ".9rem"}}>
          {cc.addOnsSubtitle}
        </p>

        <div style={{display: "flex", flexDirection: "column", gap: "12px", marginBottom: "20px"}}>
          {/* Syringes Add-on - Optional */}
          <div
            className={`aa-card ${addOns.syringes ? "selected" : ""}`}
            onClick={() => toggleAddOn('syringes')}
            style={{display:"flex", alignItems:"center", cursor:"pointer", padding:"16px"}}
            data-testid="addon-syringes"
          >
            <span className="aa-check"/>
            <div style={{flex: 1, marginLeft: "4px"}}>
              <div className="aa-card-title" style={{fontSize: "1rem"}}>{cc.syringes}</div>
              <div className="aa-card-sub">{cc.syringesDesc}</div>
            </div>
            <div style={{fontFamily:"var(--font-display)", fontSize:"1.1rem", color: addOns.syringes ? "var(--black)" : "var(--gold)"}}>
              {formatPrice(ADDON_PRICES.syringes)}
            </div>
          </div>
        </div>

        <hr className="aa-hr"/>
        <h2 className="aa-h2" style={{fontSize:"1.2rem"}}>{cc.payment}</h2>
        <div ref={cardRef} id="card-container" data-testid="square-card"
             style={{background:"var(--dark)", padding:"14px", borderRadius:"10px", border:"1px solid rgba(193,127,58,.25)"}}/>

        <hr className="aa-hr"/>
        <h2 className="aa-h2" style={{fontSize:"1.2rem"}}>{cc.orderSummary}</h2>
        <p className="aa-sub" style={{marginBottom:"16px", fontSize:".9rem"}}>
          {cc.selectedProducts}
        </p>
        {items.map(i => (
          <div
            key={i.sku}
            className={`aa-card ${selectedItems[i.sku] ? "selected" : ""}`}
            onClick={() => toggleItemSelection(i.sku)}
            style={{display:"flex", alignItems:"center", cursor:"pointer", padding:"12px", marginBottom:"8px"}}
            data-testid={`select-item-${i.sku}`}
          >
            <span className="aa-check"/>
            <div style={{flex: 1, marginLeft: "4px"}}>
              <div className="aa-card-title" style={{fontSize: ".95rem"}}>{i.name}</div>
              <div className="aa-card-sub">{cc.qty}: {i.qty}</div>
            </div>
            <div style={{fontFamily:"var(--font-display)", fontSize:"1.1rem", color: selectedItems[i.sku] ? "var(--black)" : "var(--gold)"}}>
              {formatPrice(i.price_cents * i.qty)}
            </div>
          </div>
        ))}

        {/* Mandatory shipping - always shown */}
        <div style={{display:"flex", justifyContent:"space-between", padding:"8px 0", color:"var(--muted)"}}>
          <span>{cc.shipping}</span><span>{formatPrice(MANDATORY_SHIPPING)}</span>
        </div>

        {/* Show optional add-ons in summary if selected */}
        {addOns.syringes && (
          <div style={{display:"flex", justifyContent:"space-between", padding:"8px 0", color:"var(--muted)"}}>
            <span>+ {cc.syringes}</span><span>{formatPrice(ADDON_PRICES.syringes)}</span>
          </div>
        )}

        <hr className="aa-hr" style={{margin:"12px 0"}}/>
        <div style={{display:"flex", justifyContent:"space-between", padding:"4px 0", fontSize:"1rem"}}>
          <span>{cc.subtotal}</span><span>{formatPrice(calculateSubtotal())}</span>
        </div>
        <div style={{display:"flex", justifyContent:"space-between", padding:"4px 0", fontSize:"1rem", color:"var(--muted)"}}>
          <span>{cc.tax} {form.state && `(${form.state})`}</span><span>{formatPrice(taxAmount)}</span>
        </div>
        <hr className="aa-hr" style={{margin:"12px 0"}}/>
        <div style={{display:"flex", justifyContent:"space-between", fontFamily:"var(--font-display)", fontSize:"1.3rem", color:"var(--gold)"}}>
          <span>{cc.total}</span><span data-testid="checkout-total">{formatPrice(calculateTotal())}</span>
        </div>

        {err && <div className="aa-error" data-testid="checkout-error">{err}</div>}
        <div className="aa-bottom" style={{justifyContent: "center"}}>
          <button className="aa-btn" onClick={pay} disabled={busy} data-testid="checkout-pay">{busy?cc.processing:`${cc.placeOrder} ${formatPrice(calculateTotal())}`}</button>
        </div>
        <p className="aa-caption" style={{textAlign: "center"}}>{cc.securePayment}</p>
      </div>
    </>
  );
}
